import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  DocumentChartBarIcon,
  CurrencyDollarIcon,
  ArrowUpTrayIcon,
  FunnelIcon,
  UserPlusIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  KeyIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  BellAlertIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import ChangePasswordModal from "../components/ChangePasswordModal";
import logo from "../assests/images/logo.png";

function DashboardPage() {
  const [stats, setStats] = useState({
    totalInvestors: 0,
    totalSchemes: 0,
    // INR specific stats
    totalINRInvestors: 0,
    totalReceived: 0,
    totalSent: 0,
    totalBalance: 0,
  });
  const [recentInvestors, setRecentInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [maturityAlerts, setMaturityAlerts] = useState({
    upcoming: 0,
    overdue: 0,
  });
  const navigate = useNavigate();

  // Get auth context
  const { user, logout, hasPermission, getUserRole } = useAuth();
  const username = user?.username || "Admin User";

  // Get display role
  const getUserDisplayRole = () => {
    const role = getUserRole();
    switch (role) {
      case "admin":
        return "Administrator";
      case "admin_team":
        return "Admin Team";
      case "investor":
        return "Investor";
      default:
        return "User";
    }
  };

  // Determine if user should see INR-focused dashboard
  const shouldShowINRDashboard = useMemo(() => {
    const hasINRPermission = hasPermission("inr.view");
    const hasInvestorsPermission = hasPermission("investors.view");

    // Show INR dashboard if user has INR permission but not investors permission
    // OR if user only has INR permissions (INR-focused role)
    return hasINRPermission && !hasInvestorsPermission;
  }, [hasPermission]);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login", { replace: true });
  }, [navigate, logout]);

  // Consolidated data fetching to prevent multiple state updates
  const fetchDashboardData = useCallback(async () => {
    // Don't show loading if data has been loaded before
    if (!dataLoaded) {
      setLoading(true);
    }

    try {
      if (shouldShowINRDashboard) {
        // Fetch INR-specific dashboard data
        // Handle each request separately to avoid multiple permission errors
        let statsData = {
          totalINRInvestors: 0,
          totalReceived: 0,
          totalSent: 0,
          totalBalance: 0,
          totalSchemes: 0,
        };
        let recentData = [];

        try {
          const statsResponse = await api.get(
            "/api/inr-investors/dashboard/stats"
          );
          statsData = { ...statsResponse.data, totalSchemes: 0 };
        } catch (error) {
          if (error.response?.status !== 403) {
            console.error("Failed to fetch INR stats:", error);
          }
          // Silently fail for 403 - user doesn't have permission, show zeros
        }

        try {
          const recentResponse = await api.get(
            "/api/inr-investors/dashboard/recent"
          );
          recentData = recentResponse.data;
        } catch (error) {
          if (error.response?.status !== 403) {
            console.error("Failed to fetch recent INR investors:", error);
          }
          // Silently fail for 403 - user doesn't have permission, show empty list
        }

        setStats(statsData);
        setRecentInvestors(recentData);
      } else {
        // Fetch regular dashboard data
        // Handle each request separately to avoid multiple permission errors
        let statsData = {
          totalInvestors: 0,
          totalSchemes: 0,
          totalINRInvestors: 0,
          totalReceived: 0,
          totalSent: 0,
          totalBalance: 0,
        };
        let recentData = [];

        try {
          const statsResponse = await api.get("/api/investors/dashboard/stats");
          statsData = statsResponse.data;
        } catch (error) {
          if (error.response?.status !== 403) {
            console.error("Failed to fetch investor stats:", error);
          }
          // Silently fail for 403 - user doesn't have permission, show zeros
        }

        try {
          const recentResponse = await api.get(
            "/api/investors/dashboard/recent"
          );
          recentData = recentResponse.data;
        } catch (error) {
          if (error.response?.status !== 403) {
            console.error("Failed to fetch recent investors:", error);
          }
          // Silently fail for 403 - user doesn't have permission, show empty list
        }

        setStats(statsData);
        setRecentInvestors(recentData);
      }

      setDataLoaded(true);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      // Fallback to placeholder data in case of error
      setStats({
        totalInvestors: 0,
        totalSchemes: 0,
        totalINRInvestors: 0,
        totalReceived: 0,
        totalSent: 0,
        totalBalance: 0,
      });
      setRecentInvestors([]);
      setDataLoaded(true);
    } finally {
      setLoading(false);
      if (initialLoad) {
        setInitialLoad(false);
      }
    }
  }, [initialLoad, dataLoaded, shouldShowINRDashboard]);

  // Initial data fetch only
  useEffect(() => {
    if (!dataLoaded) {
      fetchDashboardData();
    }
  }, [fetchDashboardData, dataLoaded]);

  // Fetch maturity notifications (REPNFT/UNSREP - 600 day rule)
  useEffect(() => {
    if (!hasPermission("investors.view")) return;
    let mounted = true;
    api
      .get("/api/maturity/notifications", { params: { days: 30 } })
      .then((res) => {
        if (!mounted) return;
        setMaturityAlerts({
          upcoming: res.data?.counts?.upcoming || 0,
          overdue: res.data?.counts?.overdue || 0,
        });
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      mounted = false;
    };
  }, [hasPermission]);

  // Build navigation based on user permissions
  const navigation = (() => {
    const userRole = getUserRole();
    const items = [];

    // Core Data Management
    if (hasPermission("investors.view")) {
      items.push({
        name: "Investors & Contracts",
        href: "/investors",
        icon: UsersIcon,
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
        textColor: "text-blue-600",
        description: "Manage investor data and contract stacking",
      });
    }

    if (hasPermission("inr.view")) {
      items.push({
        name: "INR Investors",
        href: "/inr-investors",
        icon: CurrencyDollarIcon,
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200",
        textColor: "text-yellow-600",
        description: "Add and manage INR investors",
      });
    }

    if (hasPermission("schemes.view")) {
      items.push({
        name: "Investment Schemes",
        href: "/schemes",
        icon: DocumentChartBarIcon,
        bgColor: "bg-purple-50",
        borderColor: "border-purple-200",
        textColor: "text-purple-600",
        description: "Manage investment schemes",
      });
    }

    // Data Entry & Upload - Crypto Investors
    if (hasPermission("investors.create")) {
      items.push({
        name: "Add Investor",
        href: "/investor-form",
        icon: UserPlusIcon,
        bgColor: "bg-pink-50",
        borderColor: "border-pink-200",
        textColor: "text-pink-600",
        description: "Add new investor record",
      });
    }

    if (hasPermission("investors.bulk_upload")) {
      items.push({
        name: "Bulk Upload",
        href: "/bulk-upload",
        icon: ArrowUpTrayIcon,
        bgColor: "bg-indigo-50",
        borderColor: "border-indigo-200",
        textColor: "text-indigo-600",
        description: "Upload CSV/XLS files",
      });
    }

    // Data Entry & Upload - INR
    if (hasPermission("inr.create")) {
      items.push({
        name: "Add INR Investor",
        href: "/inr-investor-form",
        icon: UserPlusIcon,
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
        textColor: "text-amber-600",
        description: "Add new INR investor record",
      });
    }

    if (hasPermission("inr.bulk_upload")) {
      items.push({
        name: "INR Bulk Upload",
        href: "/inr-bulk-upload",
        icon: ArrowUpTrayIcon,
        bgColor: "bg-teal-50",
        borderColor: "border-teal-200",
        textColor: "text-teal-600",
        description: "Upload INR CSV/XLS files",
      });
    }

    // Search & Filtering
    if (hasPermission("investors.view")) {
      items.push({
        name: "Filter Investors",
        href: "/investors-filter",
        icon: FunnelIcon,
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        textColor: "text-green-600",
        description: "Advanced search and filtering",
      });
    }

    if (hasPermission("inr.view")) {
      items.push({
        name: "Filter INR Investors",
        href: "/inr-investors-filter",
        icon: FunnelIcon,
        bgColor: "bg-emerald-50",
        borderColor: "border-emerald-200",
        textColor: "text-emerald-600",
        description: "Advanced INR search and filtering",
      });
    }

    if (hasPermission("investors.view")) {
      items.push({
        name: "Maturity Tracker",
        href: "/maturity",
        icon: CalendarDaysIcon,
        bgColor: "bg-indigo-50",
        borderColor: "border-indigo-200",
        textColor: "text-indigo-600",
        description:
          "REPNFT & UNSREP maturity dates (600 days) with month-wise view",
      });
    }

    // User Management (Admin only)
    if (hasPermission("users.view")) {
      items.push({
        name: "User Management",
        href: "/admin/users",
        icon: UserGroupIcon,
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        textColor: "text-red-600",
        description: "Manage users and permissions",
      });
    }

    // Investor Access (for admins and users with appropriate permissions)
    if (userRole === "admin" || hasPermission("investors.view")) {
      items.push({
        name: "Investor Access",
        href: "/investor-access",
        icon: KeyIcon,
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200",
        textColor: "text-orange-600",
        description:
          "Provide login access for investors to view their dashboard",
      });
    }

    return items;
  })();

  // Memoize stats display to prevent unnecessary re-renders
  const statsDisplay = useMemo(() => {
    if (shouldShowINRDashboard) {
      return {
        totalINRInvestors: stats.totalINRInvestors?.toLocaleString() || "0",
        totalReceived:
          stats.totalReceived?.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
          }) || "₹0.00",
        totalSent:
          stats.totalSent?.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
          }) || "₹0.00",
        totalBalance:
          stats.totalBalance?.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
          }) || "₹0.00",
      };
    } else {
      return {
        totalInvestors: stats.totalInvestors?.toLocaleString() || "0",
        totalSchemes: stats.totalSchemes || 0,
      };
    }
  }, [stats, shouldShowINRDashboard]);

  // Show loading only on initial load to prevent flickering on subsequent visits
  if (initialLoad && loading && !dataLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo and Brand */}
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <img
                    src={logo}
                    alt="Investors Portal Logo"
                    className="w-20 rounded-lg object-cover"
                    style={{ backgroundColor: "white", padding: "2px" }}
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    Investors Portal
                  </h1>
                  <p className="text-sm text-gray-500">
                    Database Management System
                  </p>
                </div>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <UserCircleIcon className="h-8 w-8 text-gray-400" />
                <div className="text-sm">
                  <p className="font-medium text-gray-900">
                    {getUserDisplayRole()}
                  </p>
                  <p className="text-gray-500">{username}</p>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors duration-200"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Maturity notifications banner */}
        {(maturityAlerts.overdue > 0 || maturityAlerts.upcoming > 0) && (
          <Link
            to="/maturity"
            className="mb-6 flex items-center justify-between gap-4 px-5 py-4 rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <BellAlertIcon className="h-6 w-6 text-orange-600" />
              <div>
                <p className="text-sm font-semibold text-orange-900">
                  Maturity Alerts (REPNFT &amp; UNSREP)
                </p>
                <p className="text-xs text-orange-700">
                  {maturityAlerts.overdue > 0 && (
                    <span className="font-medium">
                      {maturityAlerts.overdue} overdue
                    </span>
                  )}
                  {maturityAlerts.overdue > 0 &&
                    maturityAlerts.upcoming > 0 &&
                    " • "}
                  {maturityAlerts.upcoming > 0 && (
                    <span>
                      {maturityAlerts.upcoming} maturing in next 30 days
                    </span>
                  )}
                </p>
              </div>
            </div>
            <span className="text-sm font-medium text-orange-700">
              View &rarr;
            </span>
          </Link>
        )}

        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">
            {shouldShowINRDashboard
              ? "INR Investors Dashboard"
              : getUserRole() === "admin"
              ? "Admin Dashboard"
              : "Team Dashboard"}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {shouldShowINRDashboard
              ? "Overview of your INR investment management system"
              : "Overview of your investment management system"}
          </p>
        </div>

        {/* Statistics Cards */}
        {shouldShowINRDashboard ? (
          // INR-specific stats cards
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                    <UsersIcon className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">
                    Total INR Investors
                  </h3>
                  <p className="text-2xl font-semibold text-gray-900">
                    {statsDisplay.totalINRInvestors}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center">
                    <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">
                    Total Received
                  </h3>
                  <p className="text-xl font-semibold text-green-900">
                    {statsDisplay.totalReceived}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center">
                    <ArrowUpTrayIcon className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">
                    Total Sent
                  </h3>
                  <p className="text-xl font-semibold text-red-900">
                    {statsDisplay.totalSent}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-center">
                    <DocumentChartBarIcon className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">
                    Current Balance
                  </h3>
                  <p className="text-xl font-semibold text-purple-900">
                    {statsDisplay.totalBalance}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Regular stats cards
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                    <UsersIcon className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">
                    Total Investors
                  </h3>
                  <p className="text-2xl font-semibold text-gray-900">
                    {statsDisplay.totalInvestors}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center">
                    <DocumentChartBarIcon className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">
                    Investment Schemes
                  </h3>
                  <p className="text-2xl font-semibold text-gray-900">
                    {statsDisplay.totalSchemes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Management Tools - Cards show based on individual permissions */}
        {navigation.length > 0 && (
          <div className="mb-8">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Management Tools
              </h2>
              <p className="text-sm text-gray-600">
                Access all system features and tools
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="group relative bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6"
                  >
                    <div className="flex items-start space-x-4">
                      <div
                        className={`flex-shrink-0 w-12 h-12 ${item.bgColor} ${item.borderColor} border rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}
                      >
                        <Icon className={`h-6 w-6 ${item.textColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                          {item.name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Investors */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                {shouldShowINRDashboard
                  ? "Recent INR Investors"
                  : "Recent Investors"}
              </h2>
              <Link
                to={shouldShowINRDashboard ? "/inr-investors" : "/investors"}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentInvestors.length > 0 ? (
                recentInvestors.map((investor) => (
                  <div
                    key={investor.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {shouldShowINRDashboard
                          ? investor.name
                          : investor.buyer_name}
                      </h3>
                      {shouldShowINRDashboard ? (
                        <div className="text-sm text-gray-500">
                          {investor.phone_number && (
                            <span>📱 {investor.phone_number}</span>
                          )}
                          {investor.date && (
                            <span className="ml-2">
                              📅 {new Date(investor.date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          {investor.scheme}
                        </p>
                      )}
                    </div>
                    {shouldShowINRDashboard ? (
                      <div className="text-right">
                        {investor.balance !== null &&
                          investor.balance !== undefined && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              ₹
                              {parseFloat(investor.balance).toLocaleString(
                                "en-IN"
                              )}
                            </span>
                          )}
                      </div>
                    ) : (
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          investor.status === "With UNS"
                            ? "bg-green-100 text-green-800"
                            : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {investor.status}
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">
                    {shouldShowINRDashboard
                      ? "No recent INR investors found"
                      : "No recent investors found"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        userType="admin"
      />
    </div>
  );
}

export default DashboardPage;
